import React, { useRef, useState } from 'react'
import log from 'loglevel'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useReadAccess } from 'src/app/AppAuth/useReadAccess'
import { ChildEntitiesNote } from 'src/components/async/model/control/ChildEntitiesNote'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { MatchingRowsText } from 'src/components/Reusable/IndexPage/IndexPageExport'
import { useConfirmationModal } from 'src/components/ModalForms/useConfirmationModal'
import { showInfoToast, ToastPrompts } from 'src/Utils/helpers/toast'
import {
  ListedControls,
  useDownloadControlsReport,
} from '../../../../../components/async/model/control'
import { ReportExportFormat } from '../../../../../Utils/reports/ControlsReport'

export const exportFormatCopy = (
  format: ReportExportFormat,
): { formatLabel: string; fileTypeDescription: string } =>
  format === 'xlsx'
    ? { formatLabel: 'XLSX', fileTypeDescription: 'an excel (xlsx) file' }
    : { formatLabel: 'CSV', fileTypeDescription: 'a csv file' }

// A failed export closes the dialog with a toast, rather than leaving it open with nothing happening.
export const exportOrToast = async (
  download: () => Promise<void>,
): Promise<void> => {
  try {
    await download()
  } catch (error) {
    log.error('Error exporting controls', error)
    showInfoToast(ToastPrompts.CONTROLS_EXPORT_FAILED)
  }
}

const IncludeTestResultsCheckBox = ({
  includeRef,
}: {
  includeRef: React.MutableRefObject<boolean>
}): JSX.Element => {
  const [checked, setChecked] = useState(includeRef.current)
  return (
    <CheckBoxInput
      label="Include control test results"
      checked={checked}
      onChange={(event) => {
        setChecked(event.currentTarget.checked)
        includeRef.current = event.currentTarget.checked
      }}
    />
  )
}

export function useDownloadControlsReportModal(
  format: ReportExportFormat = 'csv',
  listed?: ListedControls,
): () => void {
  const download = useDownloadControlsReport(undefined, format, listed)
  const canViewTestResults = useReadAccess(CAPABILITY.CAN_VIEW_AUDIT_SCANS)
  // Only the Controls page export offers the test results, and it is the caller that passes the
  // grid's rows. The dashboard has its own Controls with Control Tests downloads.
  const offersTestResults = Boolean(listed) && canViewTestResults
  const includeTestResultsRef = useRef(false)
  const { formatLabel, fileTypeDescription } = exportFormatCopy(format)
  const listedCount = listed?.controlIds.length ?? 0

  const open = useConfirmationModal({
    title: `Export Control Detail ${formatLabel}`,
    body: (
      <>
        <p>
          Trustero will gather the data to create {fileTypeDescription} of{' '}
          {listed?.isFiltered ? (
            <>
              <MatchingRowsText count={listedCount} noun="control" />.
            </>
          ) : (
            'working controls.'
          )}
        </p>
        <ChildEntitiesNote isOmitted={listed?.isFiltered} />
        {offersTestResults && (
          <IncludeTestResultsCheckBox includeRef={includeTestResultsRef} />
        )}
      </>
    ),
    confirmText: `Export Control ${formatLabel}`,
    onConfirmCB: () =>
      exportOrToast(() =>
        download({ includeTestResults: includeTestResultsRef.current }),
      ),
  })

  return () => {
    includeTestResultsRef.current = offersTestResults
    open()
  }
}
